import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Descriptions,
  Button,
  Space,
  Tag,
  Spin,
  Alert,
  Modal,
  message,
  Statistic,
  Row,
  Col,
  Card,
} from 'antd';
import {
  EditOutlined,
  DeleteOutlined,
  ArrowLeftOutlined,
  SendOutlined,
  ClockCircleOutlined,
  SyncOutlined,
  LikeOutlined,
  ShareAltOutlined,
  MessageOutlined,
  EyeOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import {
  getSocialPost,
  deleteSocialPost,
  publishSocialPost,
  scheduleSocialPost,
  syncPostEngagement,
  type SocialPost,
} from '@/api/socialApi';
import { useAuth } from '@/hooks/useAuth';

const STATUS_COLORS: Record<string, string> = {
  draft: 'default',
  scheduled: 'orange',
  published: 'green',
  failed: 'red',
};

const PLATFORM_COLORS: Record<string, string> = {
  facebook: 'blue',
  instagram: 'magenta',
  twitter: 'cyan',
  linkedin: 'geekblue',
  wechat: 'green',
};

export default function SocialPostDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();

  const [post, setPost] = useState<SocialPost | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const canWrite = hasPermission('social_posts', 'write');
  const canDelete = hasPermission('social_posts', 'delete');

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    setError(null);
    getSocialPost(id)
      .then(setPost)
      .catch((err) => setError(err instanceof Error ? err.message : t('app.not_found')))
      .finally(() => setLoading(false));
  }, [id, t]);

  const handleDelete = useCallback(() => {
    if (!id) return;
    Modal.confirm({
      title: t('app.confirm'),
      content: t('social.action.delete_confirm_post'),
      okText: t('app.yes'),
      cancelText: t('app.no'),
      okType: 'danger',
      onOk: async () => {
        try {
          await deleteSocialPost(id);
          message.success(t('social.delete_post_success'));
          navigate('/social/posts');
        } catch {
          message.error(t('app.error'));
        }
      },
    });
  }, [id, t, navigate]);

  const handlePublish = useCallback(async () => {
    if (!id) return;
    try {
      await publishSocialPost(id);
      message.success(t('social.action.publish_success'));
      const updated = await getSocialPost(id);
      setPost(updated);
    } catch {
      message.error(t('app.error'));
    }
  }, [id, t]);

  const handleSyncEngagement = useCallback(async () => {
    if (!id) return;
    try {
      await syncPostEngagement(id);
      message.success(t('social.action.sync_engagement_success'));
      const updated = await getSocialPost(id);
      setPost(updated);
    } catch {
      message.error(t('app.error'));
    }
  }, [id, t]);

  if (loading) return <Spin size="large" />;
  if (error) return <Alert type="error" message={error} />;
  if (!post) return <Alert type="warning" message={t('app.not_found')} />;

  return (
    <div>
      <Space style={{ marginBottom: 16 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/social/posts')}>
          {t('app.back')}
        </Button>
        {canWrite && post.status !== 'published' && (
          <Button icon={<EditOutlined />} onClick={() => navigate(`/social/posts/${id}/edit`)}>
            {t('app.edit')}
          </Button>
        )}
        {canDelete && post.status !== 'published' && (
          <Button danger icon={<DeleteOutlined />} onClick={handleDelete}>
            {t('app.delete')}
          </Button>
        )}
        {post.status === 'draft' && canWrite && (
          <Button type="primary" icon={<SendOutlined />} onClick={handlePublish}>
            {t('social.action.publish')}
          </Button>
        )}
        {post.status === 'draft' && canWrite && (
          <Button icon={<ClockCircleOutlined />} onClick={() => {
            Modal.confirm({
              title: t('social.action.schedule'),
              content: t('social.field.scheduled_at'),
              okText: t('app.save'),
              cancelText: t('app.cancel'),
              onOk: async () => {
                if (!id) return;
                try {
                  const scheduledAt = new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString();
                  await scheduleSocialPost(id, scheduledAt);
                  message.success(t('social.action.schedule_success'));
                  const updated = await getSocialPost(id);
                  setPost(updated);
                } catch {
                  message.error(t('app.error'));
                }
              },
            });
          }}>
            {t('social.action.schedule')}
          </Button>
        )}
        {post.status === 'published' && canWrite && (
          <Button icon={<SyncOutlined />} onClick={handleSyncEngagement}>
            {t('social.action.sync_engagement')}
          </Button>
        )}
      </Space>

      <h2>{t('social.post_detail_title')}</h2>

      <Descriptions bordered column={2}>
        <Descriptions.Item label={t('social.field.content')} span={2}>
          {post.content}
        </Descriptions.Item>
        <Descriptions.Item label={t('social.field.platform')}>
          <Tag color={PLATFORM_COLORS[post.platform] ?? 'default'}>
            {t(`social.platform.${post.platform}`, post.platform)}
          </Tag>
        </Descriptions.Item>
        <Descriptions.Item label={t('social.field.post_type')}>
          {t(`social.post_type.${post.post_type}`, post.post_type)}
        </Descriptions.Item>
        <Descriptions.Item label={t('social.field.status')}>
          <Tag color={STATUS_COLORS[post.status] ?? 'default'}>
            {t(`social.status.${post.status}`, post.status)}
          </Tag>
        </Descriptions.Item>
        <Descriptions.Item label={t('social.field.account')}>
          <a onClick={() => navigate(`/social/accounts/${post.account_id}`)}>
            {post.account_id}
          </a>
        </Descriptions.Item>
        <Descriptions.Item label={t('social.field.scheduled_at')}>
          {post.scheduled_at ? new Date(post.scheduled_at).toLocaleString() : '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('social.field.published_at')}>
          {post.published_at ? new Date(post.published_at).toLocaleString() : '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('social.field.tags')} span={2}>
          {post.tags?.length > 0 ? post.tags.map((tag) => <Tag key={tag}>{tag}</Tag>) : '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('social.field.media_urls')} span={2}>
          {post.media_urls?.length > 0 ? post.media_urls.join(', ') : '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('social.field.branch_id')}>
          {post.branch_id || '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('social.field.external_post_id')}>
          {post.external_post_id || '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('social.field.created_by')}>
          {post.created_by}
        </Descriptions.Item>
        <Descriptions.Item label={t('social.field.created_at')}>
          {new Date(post.created_at).toLocaleString()}
        </Descriptions.Item>
        <Descriptions.Item label={t('social.field.updated_by')}>
          {post.updated_by}
        </Descriptions.Item>
        <Descriptions.Item label={t('social.field.updated_at')}>
          {new Date(post.updated_at).toLocaleString()}
        </Descriptions.Item>
      </Descriptions>

      {post.engagement && (
        <>
          <h3 style={{ marginTop: 24 }}>{t('social.section.engagement')}</h3>
          <Row gutter={16}>
            <Col span={4}>
              <Card>
                <Statistic title={t('social.field.likes')} value={post.engagement.likes} prefix={<LikeOutlined />} />
              </Card>
            </Col>
            <Col span={4}>
              <Card>
                <Statistic title={t('social.field.shares')} value={post.engagement.shares} prefix={<ShareAltOutlined />} />
              </Card>
            </Col>
            <Col span={4}>
              <Card>
                <Statistic title={t('social.field.comments')} value={post.engagement.comments} prefix={<MessageOutlined />} />
              </Card>
            </Col>
            <Col span={4}>
              <Card>
                <Statistic title={t('social.field.reach')} value={post.engagement.reach} prefix={<EyeOutlined />} />
              </Card>
            </Col>
            <Col span={4}>
              <Card>
                <Statistic title={t('social.field.impressions')} value={post.engagement.impressions} />
              </Card>
            </Col>
            <Col span={4}>
              <Card>
                <Statistic title={t('social.field.clicks')} value={post.engagement.clicks} />
              </Card>
            </Col>
          </Row>
        </>
      )}
    </div>
  );
}
